import React, { useEffect, useState } from "react";
import { MainLayout } from "components/layout/MainLayout";
import { Card, CardHeader, CardBody, Button, Modal } from "components/ui";
import { useToast } from "hooks/useToast";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/index";
import {
  AddOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { packagesService } from "services/packages.service";
import { Package, getDetailByDuration } from "types/package";
import parse from "html-react-parser";

function formatPrice(pkg: Package, isYearly: boolean): string {
  const d = getDetailByDuration(pkg, isYearly);
  const price = d?.price ?? 0;
  return price === 0 ? "0" : price + "$";
}
function validitySummary(pkg: Package): string {
  const monthly = getDetailByDuration(pkg, false);
  const yearly = getDetailByDuration(pkg, true);
  if (!monthly && !yearly) return "—";
  const parts: string[] = [];
  if (monthly) parts.push(`${monthly.validityDays}d/mo`);
  if (yearly) parts.push(`${yearly.validityDays}d/yr`);
  return parts.join(", ");
}

export const PackagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [rows, setRows] = useState<Package[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedRows = rows;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await packagesService.list(page, pageSize);
        setRows(res.data);
        setTotal(res.pagination.total);
      } catch (e: any) {
        setError(e?.message || "Failed to load packages");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const onAdd = () => {
    navigate(ROUTES.PACKAGE_NEW);
  };

  const onView = (id: string) => {
    navigate(`${ROUTES.PACKAGES}/${id}/view`);
  };

  const onEdit = (id: string) => {
    navigate(`${ROUTES.PACKAGES}/${id}/edit`);
  };

  const onRequestDelete = (id: string) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const onConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await packagesService.remove(deletingId);
      const res = await packagesService.list(page, pageSize);
      setRows(res.data);
      setTotal(res.pagination.total);
      toast.success("Package deleted successfully");
    } catch (e: any) {
      toast.error(e?.message || "Failed to delete");
    } finally {
      setConfirmOpen(false);
      setDeletingId(null);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader title="Packages" />
        <CardBody>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="text-sm text-secondary-600">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button variant="primary" size="sm" onClick={onAdd} className="w-full sm:w-auto">
                <AddOutlined fontSize="small" />
                <span>Add</span>
              </Button>
            </div>
          </div>

          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
          {isLoading && (
            <div className="mb-3 text-sm text-secondary-600">Loading...</div>
          )}

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {!isLoading && !error && pagedRows.length === 0 && (
              <div className="p-6 text-center text-secondary-600">No data</div>
            )}
            {pagedRows.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-secondary-900 text-base">
                      {pkg.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onView(pkg.id)}
                      className="p-2"
                    >
                      <VisibilityOutlined fontSize="small" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(pkg.id)}
                      className="p-2"
                    >
                      <EditOutlined fontSize="small" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Name:
                    </span>
                    <span className="text-secondary-900">{pkg.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Users:
                    </span>
                    <span className="text-secondary-900">{pkg.numberOfUsers}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Monthly Price:
                    </span>
                    <span className="text-secondary-900">
                      {formatPrice(pkg, false)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Yearly Price:
                    </span>
                    <span className="text-secondary-900">
                      {formatPrice(pkg, true)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Validity:
                    </span>
                    <span className="text-secondary-900">{validitySummary(pkg)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-secondary-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRequestDelete(pkg.id)}
                    className="flex-1"
                  >
                    <DeleteOutline fontSize="small" />
                    <span>Delete</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border border-secondary-200 rounded-md overflow-hidden">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Title
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Name
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Number of Users
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Monthly Price
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Yearly Price
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Validity (Days)
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && !error && pagedRows.length === 0 && (
                  <tr>
                    <td
                      className="p-6 text-center text-secondary-600"
                      colSpan={5}
                    >
                      No data
                    </td>
                  </tr>
                )}
                {pagedRows.map((pkg) => (
                  <tr
                    key={pkg.id}
                    className="border-t border-secondary-100 hover:bg-secondary-50"
                  >
                    <td className="p-3">{pkg.title}</td>
                    <td className="p-3">{pkg.name}</td>
                    <td className="p-3">{pkg.numberOfUsers}</td>
                    <td className="p-3">{formatPrice(pkg, false)}</td>
                    <td className="p-3">{formatPrice(pkg, true)}</td>
                    <td className="p-3">{validitySummary(pkg)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <IconButton
                          size="small"
                          onClick={() => onView(pkg.id)}
                          aria-label="View"
                        >
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(pkg.id)}
                          aria-label="Edit"
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onRequestDelete(pkg.id)}
                          aria-label="Delete"
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={page === 1}
              className="w-full sm:w-auto"
            >
              Prev
            </Button>
            <div className="text-sm text-secondary-600 text-center">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of{" "}
              {total}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={page === totalPages}
              className="w-full sm:w-auto"
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={confirmOpen}
        title="Delete Package"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={onConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingId(null);
        }}
      >
        Are you sure you want to delete this package?
      </Modal>
    </MainLayout>
  );
};
